import React, { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  CreditCard, Search, Download, Users, Loader2, 
  Upload, Camera, Image, Check, X, Printer, Send, Mail, Smartphone, Instagram, Globe, UserPlus, Phone
} from 'lucide-react';

// Estilos CSS para impressão
const printStyles = `
@media print {
  body * {
    visibility: hidden;
  }
  .print-area, .print-area * {
    visibility: visible;
  }
  .print-area {
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
  }
  .no-print {
    display: none !important;
  }
}
`;

export default function Carteirinhas() {
  const [loading, setLoading] = useState(true);
  const [students, setStudents] = useState([]);
  const [classGroups, setClassGroups] = useState([]);
  const [selectedClass, setSelectedClass] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [cardData, setCardData] = useState(null);
  const [loadingCard, setLoadingCard] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [savingLogo, setSavingLogo] = useState(false);
  const [sendDialogOpen, setSendDialogOpen] = useState(false);
  const [sendingCards, setSendingCards] = useState(false);
  const [selectedStudentsToSend, setSelectedStudentsToSend] = useState([]);
  const [sendMode, setSendMode] = useState('turma'); // 'turma' ou 'individual'
  // Estados para seleção de responsáveis
  const [responsaveisModalOpen, setResponsaveisModalOpen] = useState(false);
  const [responsaveis, setResponsaveis] = useState([]);
  const [loadingResponsaveis, setLoadingResponsaveis] = useState(false);
  const [selectedResponsavel, setSelectedResponsavel] = useState(null);
  const [studentForWhatsApp, setStudentForWhatsApp] = useState(null);
  // Estados para adicionar novo responsável
  const [addResponsavelOpen, setAddResponsavelOpen] = useState(false);
  const [newResponsavel, setNewResponsavel] = useState({
    name: '', cpf: '', cellphone: '', email: '', parentesco: 'Responsável', tipo_vinculo: 'financeiro_opcional'
  });
  const [savingResponsavel, setSavingResponsavel] = useState(false);
  const fileInputRef = useRef(null);
  const logoInputRef = useRef(null);
  const printRef = useRef(null);

  // Função para impressão
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <html>
        <head>
          <title>Carteirinha - ${cardData?.aluno?.nome || 'Aluno'}</title>
          <style>
            * { margin: 0; padding: 0; box-sizing: border-box; font-family: Arial, sans-serif; }
            body { display: flex; justify-content: center; align-items: center; min-height: 100vh; background: white; }
            .print-container { display: flex; gap: 20px; padding: 20px; }
            .card { width: 340px; height: 220px; border-radius: 16px; padding: 16px; position: relative; }
            .front { background: linear-gradient(135deg, #f3e8ff 0%, #fce7f3 100%); border: 3px solid #7B3F98; }
            .back { background: linear-gradient(135deg, #f3e8ff 0%, #dcfce7 100%); border: 3px solid #7B3F98; }
            .header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 12px; }
            .logo-text { font-size: 16px; font-weight: bold; color: #6b21a8; }
            .logo-sub { font-size: 8px; color: #6b7280; }
            .badge { background: #6b21a8; color: white; padding: 4px 12px; border-radius: 12px; font-size: 9px; font-weight: bold; }
            .content { display: flex; gap: 12px; }
            .photo { width: 80px; height: 80px; border-radius: 50%; background: #e9d5ff; display: flex; align-items: center; justify-content: center; font-size: 28px; font-weight: bold; color: #6b21a8; }
            .info { flex: 1; }
            .label { font-size: 7px; color: #6b7280; text-transform: uppercase; margin-bottom: 2px; }
            .value { font-size: 12px; font-weight: bold; color: #6b21a8; margin-bottom: 8px; }
            .row { display: flex; gap: 16px; }
            .year { color: #16a34a; }
            .qr { position: absolute; bottom: 12px; right: 12px; width: 60px; height: 60px; background: white; border-radius: 4px; display: flex; align-items: center; justify-content: center; }
            .footer { position: absolute; bottom: 8px; left: 16px; font-size: 7px; color: #6b7280; font-style: italic; }
            .back-header { margin-bottom: 8px; }
            .hashtag { font-size: 8px; color: #6b21a8; font-style: italic; }
            .legal-box { background: white; border-radius: 8px; padding: 10px; margin: 8px 0; }
            .legal-title { font-size: 10px; font-weight: bold; color: #6b21a8; margin-bottom: 4px; }
            .legal-text { font-size: 7px; color: #374151; line-height: 1.4; }
            .school-data { background: #16a34a; color: white; padding: 8px; border-radius: 0 0 12px 12px; position: absolute; bottom: 0; left: 0; right: 0; font-size: 7px; }
            @media print { body { print-color-adjust: exact; -webkit-print-color-adjust: exact; } }
          </style>
        </head>
        <body>
          <div class="print-container">
            <div class="card front">
              <div class="header">
                <div>
                  <div class="logo-text">NOVO DNA</div>
                  <div class="logo-sub">INSTITUTO DE APRENDIZAGEM</div>
                </div>
                <div class="badge">CARTEIRA DIGITAL</div>
              </div>
              <div class="content">
                <div class="photo">${cardData?.aluno?.nome?.split(' ').slice(0, 2).map(n => n[0]).join('') || 'NA'}</div>
                <div class="info">
                  <div class="label">NOME DO ALUNO</div>
                  <div class="value">${cardData?.aluno?.nome || ''}</div>
                  <div class="row">
                    <div><div class="label">RM:</div><div class="value">${cardData?.aluno?.matricula || ''}</div></div>
                    <div><div class="label">RA:</div><div class="value">${cardData?.aluno?.ra || ''}</div></div>
                  </div>
                  <div class="row">
                    <div><div class="label">TURMA</div><div class="value">${cardData?.aluno?.turma?.split(' - ')[0] || ''}</div></div>
                    <div><div class="label">SEGMENTO</div><div class="value">${cardData?.aluno?.turma?.includes('E.I') ? 'Ed. Infantil' : 'Fundamental I'}</div></div>
                  </div>
                  <div class="row">
                    <div><div class="label">ANO LETIVO</div><div class="value year">2026</div></div>
                    <div><div class="label">VALIDADE</div><div class="value">31/12/2026</div></div>
                  </div>
                </div>
              </div>
              <div class="qr">QR</div>
              <div class="footer">"Transformando vidas através da educação de excelência"</div>
            </div>
            <div class="card back">
              <div class="back-header">
                <div class="logo-text">NOVO DNA</div>
                <div class="logo-sub">INSTITUTO DE APRENDIZAGEM</div>
                <div class="hashtag">#muitasmentesumamissão</div>
              </div>
              <div class="legal-box">
                <div class="legal-title">DOCUMENTO ESTUDANTIL - MEIA-ENTRADA</div>
                <div class="legal-text">Este documento garante ao estudante o direito à meia-entrada em eventos artísticos, culturais, esportivos e de lazer, conforme a Lei Federal nº 12.933/2013 regulamentada pelo Decreto nº 8.537/2015.</div>
              </div>
              <div class="qr" style="top: 70px;">QR</div>
              <div class="school-data">
                <strong>DADOS DA ESCOLA:</strong> Est. Benedito Cesario de Oliveira, 156/202 - Vila Iasi - Taboão da Serra - SP | Tel: (11) 5194-4993 | @novodnainstituto
              </div>
            </div>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };

  useEffect(() => {
    loadData();
    loadSchoolLogo();
  }, []);

  const loadData = async () => {
    try {
      const [studentsRes, classGroupsRes] = await Promise.all([
        api.get('/students'),
        api.get('/class-groups')
      ]);
      setStudents(studentsRes.data.filter(s => s.situation === 'Ativo'));
      setClassGroups(classGroupsRes.data);
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const loadSchoolLogo = async () => {
    try {
      const response = await api.get('/settings/school-logo');
      if (response.data?.logo_url) {
        setSchoolLogo(response.data.logo_url);
      }
    } catch (error) {}
  };

  const handleSelectStudent = async (student) => {
    setSelectedStudent(student);
    setLoadingCard(true);
    setDialogOpen(true);
    try {
      const response = await api.get(`/carteirinha/${student.id}`);
      setCardData(response.data);
    } catch (error) {
      toast.error('Erro ao carregar carteirinha');
    } finally {
      setLoadingCard(false);
    }
  };

  const handleDownloadPDF = async () => {
    if (!selectedStudent) return;
    try {
      const response = await api.get(`/carteirinha/${selectedStudent.id}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `carteirinha_${selectedStudent.registration_number}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF baixado!');
    } catch (error) {
      toast.error('Erro ao baixar PDF');
    }
  };

  const handleUploadPhoto = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedStudent) return;
    const formData = new FormData();
    formData.append('photo', file);
    try {
      await api.post(`/students/${selectedStudent.id}/photo`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success('Foto atualizada!');
      await loadData();
      const response = await api.get(`/carteirinha/${selectedStudent.id}`);
      setCardData(response.data);
    } catch (error) {
      toast.error('Erro ao fazer upload');
    }
  };

  const handleUploadLogo = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('logo', file);
    setSavingLogo(true);
    try {
      const response = await api.post('/settings/school-logo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setSchoolLogo(response.data.logo_url);
      toast.success('Logo atualizado!');
    } catch (error) {
      toast.error('Erro no upload');
    } finally {
      setSavingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    setSavingLogo(true);
    try {
      await api.delete('/settings/school-logo');
      setSchoolLogo(null);
      toast.success('Logo removido!');
    } catch (error) {
      toast.error('Erro ao remover');
    } finally {
      setSavingLogo(false);
    }
  };

  const handleSendCards = async () => {
    if (sendMode === 'turma' && selectedClass === 'all') {
      toast.error('Selecione uma turma específica');
      return;
    }
    if (sendMode === 'individual' && selectedStudentsToSend.length === 0) {
      toast.error('Selecione pelo menos um aluno');
      return;
    }

    setSendingCards(true);
    try {
      const studentIds = sendMode === 'turma' 
        ? filteredStudents.map(s => s.id)
        : selectedStudentsToSend;

      const response = await api.post('/carteirinhas/send-batch', {
        student_ids: studentIds,
        send_mode: sendMode
      });

      toast.success(`${response.data.sent_count} carteirinhas enviadas com sucesso!`);
      setSendDialogOpen(false);
      setSelectedStudentsToSend([]);
    } catch (error) {
      toast.error('Erro ao enviar carteirinhas: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSendingCards(false);
    }
  };

  const toggleStudentSelection = (studentId) => {
    setSelectedStudentsToSend(prev => 
      prev.includes(studentId) 
        ? prev.filter(id => id !== studentId)
        : [...prev, studentId]
    );
  };

  // Função para carregar responsáveis de um aluno
  const loadResponsaveis = async (studentId) => {
    setLoadingResponsaveis(true);
    try {
      const response = await api.get(`/students/${studentId}/responsaveis`);
      setResponsaveis(response.data.responsaveis || []);
    } catch (error) {
      toast.error('Erro ao carregar responsáveis');
      setResponsaveis([]);
    } finally {
      setLoadingResponsaveis(false);
    }
  };

  // Função para abrir modal de seleção de responsável
  const openWhatsAppModal = async (student) => {
    setStudentForWhatsApp(student);
    setSelectedResponsavel(null);
    setResponsaveisModalOpen(true);
    await loadResponsaveis(student.id);
  };

  // Função para enviar via WhatsApp para o responsável selecionado
  const sendWhatsAppToResponsavel = async () => {
    if (!studentForWhatsApp) return;
    
    try {
      const params = selectedResponsavel ? `?responsible_id=${selectedResponsavel}` : '';
      const response = await api.get(`/carteirinhas/whatsapp-link/${studentForWhatsApp.id}${params}`);
      
      if (response.data.whatsapp_link) {
        // Copiar link para área de transferência
        await navigator.clipboard.writeText(response.data.whatsapp_link);
        toast.success('Link copiado! Cole no WhatsApp para enviar.');
        
        // Tentar abrir o WhatsApp
        window.open(response.data.whatsapp_link, '_blank');
        setResponsaveisModalOpen(false);
      } else {
        toast.error('WhatsApp do responsável não cadastrado');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao gerar link');
    }
  };

  // Função para adicionar novo responsável
  const handleAddResponsavel = async () => {
    if (!studentForWhatsApp?.id && !selectedStudent?.id) {
      toast.error('Selecione um aluno primeiro');
      return;
    }
    
    if (!newResponsavel.name || !newResponsavel.cellphone) {
      toast.error('Nome e celular são obrigatórios');
      return;
    }

    setSavingResponsavel(true);
    try {
      const studentId = studentForWhatsApp?.id || selectedStudent?.id;
      const response = await api.post(`/students/${studentId}/add-responsavel`, newResponsavel);
      
      toast.success(response.data.message);
      setAddResponsavelOpen(false);
      setNewResponsavel({
        name: '', cpf: '', cellphone: '', email: '', parentesco: 'Responsável', tipo_vinculo: 'financeiro_opcional'
      });
      
      // Recarregar responsáveis
      await loadResponsaveis(studentId);
    } catch (error) {
      toast.error('Erro ao adicionar responsável: ' + (error.response?.data?.detail || error.message));
    } finally {
      setSavingResponsavel(false);
    }
  };

  const filteredStudents = students.filter(s => {
    const matchesClass = selectedClass === 'all' || s.class_group_id === selectedClass || s.class_name === selectedClass;
    const matchesSearch = s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         s.registration_number.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesClass && matchesSearch;
  });

  const getSegmento = (turma) => {
    if (!turma) return 'Fundamental';
    if (turma.includes('E.I') || turma.toLowerCase().includes('infantil')) return 'Educação Infantil';
    if (turma.includes('E.F1')) return 'Fundamental I';
    if (turma.includes('E.F2')) return 'Fundamental II';
    if (turma.includes('E.M')) return 'Ensino Médio';
    return 'Fundamental';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  // ====== CARTEIRINHA DIGITAL - FRENTE ======
  const DigitalCardFront = ({ data, logo }) => {
    if (!data) return null;
    const segmento = getSegmento(data.aluno.turma);
    
    return (
      <div className="relative w-[420px] h-[265px] rounded-2xl overflow-hidden shadow-2xl bg-white" style={{
        boxShadow: '0 25px 50px -12px rgba(124, 58, 237, 0.25), 0 0 0 1px rgba(124, 58, 237, 0.1)'
      }}>
        {/* Padrão geométrico sutil */}
        <div className="absolute inset-0 opacity-[0.02]">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="gridFront" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#7B3F98" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#gridFront)" />
          </svg>
        </div>
        
        {/* Barra lateral esquerda */}
        <div className="absolute left-0 top-0 bottom-0 w-2 bg-gradient-to-b from-purple-600 via-purple-500 to-green-500"/>
        
        {/* Header */}
        <div className="relative px-5 pt-4 pb-3 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-3">
            {/* Logo com fundo branco */}
            <div className="w-12 h-12 rounded-xl bg-white border-2 border-purple-200 flex items-center justify-center shadow-sm overflow-hidden">
              {logo ? (
                <img src={logo} alt="Logo" className="w-10 h-10 object-contain"/>
              ) : (
                <span className="text-purple-600 font-black text-sm">DNA</span>
              )}
            </div>
            <div>
              <h1 className="text-[11px] font-bold text-purple-900 tracking-wide leading-tight">NOVO DNA</h1>
              <p className="text-[9px] text-gray-500 font-medium">INSTITUTO DE APRENDIZAGEM</p>
            </div>
          </div>
          <div className="px-2.5 py-1 bg-gradient-to-r from-purple-600 to-purple-700 rounded-lg">
            <span className="text-[9px] font-bold text-white tracking-wider">CARTEIRA DIGITAL</span>
          </div>
        </div>
        
        {/* Conteúdo principal */}
        <div className="relative px-5 pt-3 pb-4 flex gap-4">
          {/* Foto do aluno */}
          <div className="flex-shrink-0">
            <div 
              className="w-24 h-28 rounded-xl overflow-hidden border-2 border-purple-200 shadow-lg cursor-pointer group relative"
              onClick={() => fileInputRef.current?.click()}
              style={{ background: 'linear-gradient(135deg, #f3e8ff 0%, #ecfdf5 100%)' }}
            >
              {data.aluno.foto_url ? (
                <img src={data.aluno.foto_url} alt="" className="w-full h-full object-cover"/>
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center">
                  <Camera className="w-6 h-6 text-purple-400 mb-1 group-hover:scale-110 transition-transform"/>
                  <span className="text-2xl font-bold text-purple-600">
                    {data.aluno.nome.split(' ').slice(0,2).map(n=>n[0]).join('')}
                  </span>
                </div>
              )}
            </div>
          </div>
          
          {/* Informações do aluno */}
          <div className="flex-1 flex flex-col justify-between min-w-0">
            <div className="mb-2">
              <p className="text-[8px] text-gray-400 font-semibold tracking-widest uppercase mb-0.5">NOME DO ALUNO</p>
              <p className="text-sm font-bold text-gray-900 leading-tight truncate" title={data.aluno.nome}>
                {data.aluno.nome}
              </p>
            </div>
            
            {/* RM e RA na mesma linha para economizar espaço */}
            <div className="flex items-center gap-4 mb-1.5">
              <div className="flex items-center gap-1.5">
                <span className="text-[7px] text-gray-400 font-semibold tracking-widest uppercase">RM:</span>
                <span className="text-xs font-bold text-purple-700">{data.aluno.matricula}</span>
              </div>
              <div className="w-px h-3 bg-gray-300"/>
              <div className="flex items-center gap-1.5">
                <span className="text-[7px] text-gray-400 font-semibold tracking-widest uppercase">RA:</span>
                <span className="text-xs font-bold text-purple-700 truncate max-w-[100px]" title={data.aluno.ra || '—'}>{data.aluno.ra || '—'}</span>
              </div>
            </div>
            
            <div className="grid grid-cols-2 gap-x-4 gap-y-1.5">
              <div>
                <p className="text-[7px] text-gray-400 font-semibold tracking-widest uppercase">TURMA</p>
                <p className="text-xs font-semibold text-gray-800 truncate">{data.aluno.turma}</p>
              </div>
              <div>
                <p className="text-[7px] text-gray-400 font-semibold tracking-widest uppercase">SEGMENTO</p>
                <p className="text-xs font-semibold text-gray-800">{segmento}</p>
              </div>
            </div>
            
            <div className="flex items-center justify-between mt-2 pt-2 border-t border-gray-100">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-[7px] text-gray-400 font-semibold tracking-widest uppercase">ANO LETIVO</p>
                  <p className="text-sm font-black text-green-600">{data.ano_letivo}</p>
                </div>
                <div className="w-px h-6 bg-gray-200"/>
                <div>
                  <p className="text-[7px] text-gray-400 font-semibold tracking-widest uppercase">VALIDADE</p>
                  <p className="text-xs font-bold text-gray-700">{data.validade}</p>
                </div>
              </div>
            </div>
          </div>
          
          {/* QR Code do Site */}
          <div className="flex-shrink-0 flex flex-col items-center justify-end">
            <div className="bg-white p-1.5 rounded-lg shadow-md border border-gray-100">
              <img 
                src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=https://www.novodna.com.br`} 
                alt="QR Code Site" 
                className="w-14 h-14"
              />
            </div>
            <div className="flex items-center gap-1 mt-1">
              <Globe className="w-3 h-3 text-purple-500"/>
              <p className="text-[6px] text-gray-400 font-medium">novodna.com.br</p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="absolute bottom-0 left-0 right-0 px-5 py-2 bg-gradient-to-r from-gray-50 to-white border-t border-gray-100">
          <p className="text-[8px] text-center text-gray-400 font-medium italic tracking-wide">
            "Transformando vidas através da educação de excelência"
          </p>
        </div>
      </div>
    );
  };

  // ====== CARTEIRINHA DIGITAL - VERSO ======
  const DigitalCardBack = ({ data, logo }) => {
    if (!data) return null;
    
    return (
      <div className="relative w-[420px] h-[265px] rounded-2xl overflow-hidden shadow-2xl bg-white" style={{
        boxShadow: '0 25px 50px -12px rgba(124, 58, 237, 0.25), 0 0 0 1px rgba(124, 58, 237, 0.1)'
      }}>
        {/* Padrão geométrico sutil */}
        <div className="absolute inset-0 opacity-[0.02]">
          <svg width="100%" height="100%">
            <defs>
              <pattern id="gridBack" width="20" height="20" patternUnits="userSpaceOnUse">
                <path d="M 20 0 L 0 0 0 20" fill="none" stroke="#22c55e" strokeWidth="0.5"/>
              </pattern>
            </defs>
            <rect width="100%" height="100%" fill="url(#gridBack)" />
          </svg>
        </div>
        
        {/* Barra lateral direita */}
        <div className="absolute right-0 top-0 bottom-0 w-2 bg-gradient-to-b from-green-500 via-green-400 to-purple-600"/>
        
        {/* Header */}
        <div className="relative px-5 pt-3 pb-2 flex items-center justify-between border-b border-gray-100">
          <div className="flex items-center gap-2">
            {/* Logo com fundo branco */}
            <div className="w-9 h-9 rounded-lg bg-white border-2 border-green-200 flex items-center justify-center shadow-sm overflow-hidden">
              {logo ? (
                <img src={logo} alt="Logo" className="w-7 h-7 object-contain"/>
              ) : (
                <span className="text-green-600 font-black text-[9px]">DNA</span>
              )}
            </div>
            <div>
              <h1 className="text-[10px] font-bold text-gray-800">NOVO DNA INSTITUTO DE APRENDIZAGEM</h1>
              <p className="text-[8px] text-purple-600 font-semibold">#muitasmentesumamissão</p>
            </div>
          </div>
        </div>
        
        {/* Conteúdo */}
        <div className="relative px-5 pt-2 pb-10 flex gap-3">
          {/* Coluna esquerda - Informações */}
          <div className="flex-1 space-y-2">
            {/* Lei da Meia-Entrada */}
            <div className="bg-purple-50/70 rounded-lg p-2 border border-purple-200">
              <p className="text-[7px] text-purple-700 font-bold tracking-wide uppercase mb-0.5">📜 DOCUMENTO ESTUDANTIL - MEIA-ENTRADA</p>
              <p className="text-[6.5px] text-gray-600 leading-relaxed">
                Este documento garante ao estudante o direito à <strong>meia-entrada</strong> em eventos artísticos, culturais, esportivos e de lazer, 
                conforme a <strong>Lei Federal nº 12.933/2013</strong> regulamentada pelo <strong>Decreto nº 8.537/2015</strong>. 
                O benefício é válido em todo território nacional e deve ser apresentado junto a um documento de identificação com foto.
              </p>
            </div>
            
            {/* Dados da escola */}
            <div className="bg-gray-50 rounded-lg p-2">
              <p className="text-[6.5px] text-gray-400 font-bold tracking-widest uppercase mb-0.5">DADOS DA ESCOLA</p>
              <div className="space-y-0.5 text-[7px]">
                <div className="flex">
                  <span className="text-gray-500 w-12">Endereço:</span>
                  <span className="text-gray-700 flex-1 leading-tight">{data.escola.endereco}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-12">Telefone:</span>
                  <span className="text-gray-700 font-medium">{data.escola.telefone}</span>
                </div>
                <div className="flex">
                  <span className="text-gray-500 w-12">Website:</span>
                  <span className="text-purple-600 font-medium">{data.escola.website}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Coluna direita - QR Code Instagram */}
          <div className="w-28 flex flex-col items-center justify-start">
            <div className="bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 p-0.5 rounded-xl">
              <div className="bg-white p-1 rounded-xl">
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=60x60&data=https://instagram.com/novodnainstituto`} 
                  alt="QR Instagram" 
                  className="w-14 h-14"
                />
              </div>
            </div>
            <div className="flex items-center gap-1 mt-1">
              <Instagram className="w-3 h-3 text-pink-500"/>
              <span className="text-[8px] font-semibold text-gray-700">@novodnainstituto</span>
            </div>
            <p className="text-[6px] text-gray-400 mt-0.5">Siga-nos nas redes</p>
          </div>
        </div>
        
        {/* Footer gradiente */}
        <div className="absolute bottom-0 left-0 right-0 px-5 py-2 bg-gradient-to-r from-purple-600 to-green-500">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Smartphone className="w-3 h-3 text-white/80"/>
              <p className="text-[8px] text-white font-medium">
                Carteirinha Digital - Apresente pelo celular
              </p>
            </div>
            <p className="text-[8px] text-white/80">
              {data.ano_letivo} © Novo DNA Instituto
            </p>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="carteirinhas-page">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-3 text-gray-800">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center shadow-lg">
              <CreditCard className="w-5 h-5 text-white" />
            </div>
            Carteirinhas Digitais
          </h1>
          <p className="text-gray-500 mt-1">Carteirinhas digitais para meia-entrada - Envie para as famílias!</p>
        </div>
        
        {/* Upload Logo + Botão Enviar */}
        <div className="flex gap-3">
          <Card className="border-2 border-dashed border-purple-200 bg-gradient-to-br from-purple-50/50 to-white">
            <CardContent className="p-3">
              <div className="flex items-center gap-3">
                <div 
                  className="w-12 h-12 bg-white rounded-lg border-2 border-purple-200 flex items-center justify-center cursor-pointer hover:border-purple-400 transition-all overflow-hidden"
                  onClick={() => logoInputRef.current?.click()}
                >
                  {schoolLogo ? (
                    <img src={schoolLogo} alt="Logo" className="w-full h-full object-contain p-1" />
                  ) : (
                    <Image className="w-5 h-5 text-purple-300" />
                  )}
                </div>
                <input ref={logoInputRef} type="file" accept="image/*" onChange={handleUploadLogo} className="hidden" />
                <div>
                  <p className="text-xs font-semibold text-purple-800">Logo da Escola</p>
                  <div className="flex gap-1 mt-1">
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-purple-600" onClick={() => logoInputRef.current?.click()}>
                      {schoolLogo ? 'Trocar' : 'Upload'}
                    </Button>
                    {schoolLogo && (
                      <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-red-500" onClick={handleRemoveLogo}>
                        <X className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {/* Botão Enviar para Famílias */}
          <Button 
            className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 shadow-lg"
            onClick={() => setSendDialogOpen(true)}
          >
            <Send className="w-4 h-4 mr-2"/>
            Enviar para Famílias
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex gap-3 items-center">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            placeholder="Buscar por nome ou matrícula..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 border-gray-200 focus:border-purple-400 focus:ring-purple-400"
          />
        </div>
        <Select value={selectedClass} onValueChange={setSelectedClass}>
          <SelectTrigger className="w-[220px] border-gray-200">
            <SelectValue placeholder="Filtrar turma" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as turmas</SelectItem>
            {classGroups.map(cg => (
              <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Badge className="bg-purple-100 text-purple-700 border-0 px-3 py-2">
          <Users className="w-4 h-4 mr-1" />
          {filteredStudents.length} alunos
        </Badge>
      </div>

      {/* Lista de Alunos */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filteredStudents.map(student => (
          <Card 
            key={student.id} 
            className="cursor-pointer hover:shadow-lg hover:border-purple-300 transition-all group border-gray-100"
            onClick={() => handleSelectStudent(student)}
          >
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl overflow-hidden flex-shrink-0 shadow-md group-hover:shadow-lg transition-shadow">
                  <img src={student.photo_url || "/default-avatar.png"} onError={(e) => { e.currentTarget.src = "/default-avatar.png"; }} alt="" className="w-full h-full object-cover" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-800 truncate text-sm">{student.name}</p>
                  <p className="text-xs text-purple-600 font-medium">RM: {student.registration_number}</p>
                  <p className="text-xs text-gray-500 truncate">{student.class_name}</p>
                </div>
                <Smartphone className="w-5 h-5 text-gray-300 group-hover:text-green-500 transition-colors" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredStudents.length === 0 && (
        <div className="text-center py-16 text-gray-400">
          <Users className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-medium">Nenhum aluno encontrado</p>
          <p className="text-sm">Tente ajustar os filtros de busca</p>
        </div>
      )}

      {/* Modal da Carteirinha */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-5xl bg-gradient-to-br from-gray-50 to-white">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-800 flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-600 to-purple-700 flex items-center justify-center">
                <Smartphone className="w-4 h-4 text-white" />
              </div>
              Carteirinha Digital
            </DialogTitle>
          </DialogHeader>
          
          {loadingCard ? (
            <div className="flex items-center justify-center h-80">
              <div className="text-center">
                <Loader2 className="w-10 h-10 animate-spin text-purple-600 mx-auto mb-3" />
                <p className="text-gray-500">Gerando carteirinha...</p>
              </div>
            </div>
          ) : cardData && (
            <div className="space-y-6">
              {/* Preview das Carteirinhas */}
              <div className="flex flex-col lg:flex-row gap-8 justify-center items-center py-6">
                <div className="transform hover:scale-[1.02] transition-transform">
                  <p className="text-xs text-gray-400 text-center mb-2 font-medium">FRENTE</p>
                  <DigitalCardFront data={cardData} logo={schoolLogo} />
                </div>
                <div className="transform hover:scale-[1.02] transition-transform">
                  <p className="text-xs text-gray-400 text-center mb-2 font-medium">VERSO</p>
                  <DigitalCardBack data={cardData} logo={schoolLogo} />
                </div>
              </div>

              <input ref={fileInputRef} type="file" accept="image/*" onChange={handleUploadPhoto} className="hidden"/>

              {/* Ações */}
              <div className="flex justify-center gap-3 flex-wrap">
                <Button onClick={handleDownloadPDF} className="bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 shadow-lg">
                  <Download className="w-4 h-4 mr-2"/>
                  Baixar PDF
                </Button>
                <Button variant="outline" className="border-purple-200 text-purple-700 hover:bg-purple-50" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="w-4 h-4 mr-2"/>
                  {cardData.aluno.foto_url ? 'Trocar Foto' : 'Adicionar Foto'}
                </Button>
                <Button variant="outline" className="border-green-200 text-green-700 hover:bg-green-50" onClick={handlePrint}>
                  <Printer className="w-4 h-4 mr-2"/>
                  Imprimir
                </Button>
                {/* Botão WhatsApp - Abre modal de seleção de responsável */}
                <Button 
                  className="bg-gradient-to-r from-green-500 to-green-600"
                  onClick={() => openWhatsAppModal(selectedStudent)}
                >
                  <Smartphone className="w-4 h-4 mr-2"/>
                  WhatsApp
                </Button>
                {/* Botão E-mail */}
                <Button 
                  variant="outline"
                  className="border-purple-200 text-purple-700 hover:bg-purple-50"
                  onClick={async () => {
                    try {
                      await api.post('/carteirinhas/send-single', { student_id: selectedStudent.id });
                      toast.success('Carteirinha enviada para a família!');
                    } catch (error) {
                      toast.error('Erro ao enviar');
                    }
                  }}
                >
                  <Mail className="w-4 h-4 mr-2"/>
                  E-mail
                </Button>
              </div>

              {/* Dados Completos */}
              <div className="bg-gray-50 rounded-xl p-5 border border-gray-100">
                <p className="font-semibold text-gray-700 mb-4 flex items-center gap-2">
                  <Users className="w-4 h-4 text-purple-600" />
                  Informações do Aluno
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                  {[
                    { label: 'Nascimento', value: cardData.aluno.data_nascimento || '—' },
                    { label: 'CPF', value: cardData.aluno.cpf || '—' },
                    { label: 'RG', value: cardData.aluno.rg || '—' },
                    { label: 'RA', value: cardData.aluno.ra || '—', highlight: true, color: 'purple' },
                    { label: 'Matrícula', value: cardData.aluno.matricula, highlight: true, color: 'green' },
                  ].map((item, i) => (
                    <div key={i} className={`rounded-lg p-3 ${item.highlight ? (item.color === 'purple' ? 'bg-purple-50 border border-purple-200' : 'bg-green-50 border border-green-200') : 'bg-white border border-gray-100'}`}>
                      <p className={`text-[10px] font-semibold tracking-wider uppercase ${item.highlight ? (item.color === 'purple' ? 'text-purple-600' : 'text-green-600') : 'text-gray-400'}`}>{item.label}</p>
                      <p className={`font-bold text-sm mt-0.5 ${item.highlight ? (item.color === 'purple' ? 'text-purple-800' : 'text-green-800') : 'text-gray-800'}`}>{item.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Modal Enviar para Famílias */}
      <Dialog open={sendDialogOpen} onOpenChange={setSendDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="w-5 h-5 text-green-600"/>
              Enviar Carteirinhas para Famílias
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Modo de envio - Turma ou Individual */}
            <div className="space-y-2">
              <Label>Selecionar Por:</Label>
              <div className="flex gap-3">
                <Button 
                  variant={sendMode === 'turma' ? 'default' : 'outline'}
                  onClick={() => setSendMode('turma')}
                  className={sendMode === 'turma' ? 'bg-purple-600' : ''}
                >
                  <Users className="w-4 h-4 mr-2"/>
                  Por Turma
                </Button>
                <Button 
                  variant={sendMode === 'individual' ? 'default' : 'outline'}
                  onClick={() => setSendMode('individual')}
                  className={sendMode === 'individual' ? 'bg-purple-600' : ''}
                >
                  <CreditCard className="w-4 h-4 mr-2"/>
                  Individual
                </Button>
              </div>
            </div>

            {/* Seleção de turma para envio em lote */}
            {sendMode === 'turma' && (
              <div className="space-y-2">
                <Label>Selecione a Turma</Label>
                <Select value={selectedClass} onValueChange={setSelectedClass}>
                  <SelectTrigger>
                    <SelectValue placeholder="Escolha a turma..." />
                  </SelectTrigger>
                  <SelectContent>
                    {classGroups.map(cg => (
                      <SelectItem key={cg.id} value={cg.id}>{cg.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedClass !== 'all' && (
                  <p className="text-sm text-green-600 font-medium">
                    ✓ {filteredStudents.length} alunos receberão a carteirinha
                  </p>
                )}
              </div>
            )}

            {/* Seleção individual */}
            {sendMode === 'individual' && (
              <div className="space-y-2">
                <Label>Selecione os Alunos</Label>
                <div className="border rounded-lg max-h-60 overflow-y-auto p-2 space-y-1">
                  {filteredStudents.map(student => (
                    <div 
                      key={student.id}
                      className={`flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50 ${
                        selectedStudentsToSend.includes(student.id) ? 'bg-purple-50 border border-purple-200' : ''
                      }`}
                      onClick={() => toggleStudentSelection(student.id)}
                    >
                      <Checkbox 
                        checked={selectedStudentsToSend.includes(student.id)}
                        onCheckedChange={() => toggleStudentSelection(student.id)}
                      />
                      <span className="text-sm">{student.name}</span>
                      <span className="text-xs text-gray-500 ml-auto">{student.class_name}</span>
                    </div>
                  ))}
                </div>
                <p className="text-sm text-purple-600">
                  {selectedStudentsToSend.length} aluno(s) selecionado(s)
                </p>
              </div>
            )}

            {/* Botões de envio - WhatsApp e E-mail */}
            <div className="space-y-3 pt-2 border-t">
              <Label className="text-base font-semibold">Canal de Envio:</Label>
              
              {/* Botão WhatsApp */}
              <Button 
                className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700"
                onClick={async () => {
                  const studentIds = sendMode === 'turma' 
                    ? filteredStudents.map(s => s.id)
                    : selectedStudentsToSend;
                  
                  if (studentIds.length === 0) {
                    toast.error('Selecione pelo menos um aluno');
                    return;
                  }
                  
                  setSendingCards(true);
                  try {
                    const response = await api.post('/carteirinhas/whatsapp-links', {
                      student_ids: studentIds,
                      send_mode: sendMode
                    });
                    
                    if (response.data.links && response.data.links.length > 0) {
                      // Abrir links do WhatsApp em novas abas
                      response.data.links.forEach((link, index) => {
                        setTimeout(() => {
                          window.open(link.whatsapp_link, '_blank');
                        }, index * 500); // Delay para não bloquear popup
                      });
                      toast.success(`${response.data.links.length} link(s) de WhatsApp gerado(s)!`);
                      setSendDialogOpen(false);
                    } else {
                      toast.error('Nenhum responsável com WhatsApp cadastrado');
                    }
                    
                    if (response.data.errors?.length > 0) {
                      response.data.errors.forEach(err => toast.error(err));
                    }
                  } catch (error) {
                    toast.error('Erro ao gerar links: ' + (error.response?.data?.detail || error.message));
                  } finally {
                    setSendingCards(false);
                  }
                }}
                disabled={sendingCards || (sendMode === 'turma' && selectedClass === 'all') || (sendMode === 'individual' && selectedStudentsToSend.length === 0)}
              >
                {sendingCards ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Gerando Links...</>
                ) : (
                  <><Smartphone className="w-4 h-4 mr-2"/>Enviar por WhatsApp</>
                )}
              </Button>
              
              {/* Botão E-mail */}
              <Button 
                variant="outline"
                className="w-full border-purple-200 text-purple-700 hover:bg-purple-50"
                onClick={handleSendCards}
                disabled={sendingCards || (sendMode === 'turma' && selectedClass === 'all') || (sendMode === 'individual' && selectedStudentsToSend.length === 0)}
              >
                {sendingCards ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Enviando...</>
                ) : (
                  <><Mail className="w-4 h-4 mr-2"/>Enviar por E-mail</>
                )}
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              📱 WhatsApp: Abre links para envio manual com carteirinha digital formatada<br/>
              📧 E-mail: Envia automaticamente para os e-mails cadastrados
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Seleção de Responsável para WhatsApp */}
      <Dialog open={responsaveisModalOpen} onOpenChange={setResponsaveisModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5 text-green-600"/>
              Enviar Carteirinha por WhatsApp
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {studentForWhatsApp && (
              <div className="bg-purple-50 p-3 rounded-lg">
                <p className="font-medium text-purple-800">{studentForWhatsApp.name}</p>
                <p className="text-sm text-purple-600">{studentForWhatsApp.class_name}</p>
              </div>
            )}

            <div className="space-y-2">
              <Label className="text-base font-semibold">Selecione o Responsável:</Label>
              
              {loadingResponsaveis ? (
                <div className="flex items-center justify-center py-6">
                  <Loader2 className="w-6 h-6 animate-spin text-purple-600"/>
                </div>
              ) : responsaveis.length === 0 ? (
                <div className="text-center py-6 text-gray-500">
                  <p>Nenhum responsável cadastrado</p>
                  <Button 
                    variant="outline" 
                    className="mt-3"
                    onClick={() => setAddResponsavelOpen(true)}
                  >
                    <UserPlus className="w-4 h-4 mr-2"/>
                    Adicionar Responsável
                  </Button>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {responsaveis.map((resp, index) => (
                    <div 
                      key={resp.id || index}
                      className={`p-3 rounded-lg border cursor-pointer transition-all ${
                        selectedResponsavel === resp.id 
                          ? 'bg-green-50 border-green-300 shadow-sm' 
                          : 'bg-white border-gray-200 hover:bg-gray-50'
                      }`}
                      onClick={() => setSelectedResponsavel(resp.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-gray-800">{resp.name}</p>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {resp.tipo}
                            </Badge>
                            <span className="text-xs text-gray-500">{resp.parentesco}</span>
                          </div>
                        </div>
                        <div className="text-right">
                          {resp.whatsapp ? (
                            <div className="flex items-center gap-1 text-green-600">
                              <Phone className="w-3 h-3"/>
                              <span className="text-sm">{resp.whatsapp}</span>
                            </div>
                          ) : (
                            <span className="text-xs text-red-500">Sem WhatsApp</span>
                          )}
                        </div>
                      </div>
                      {selectedResponsavel === resp.id && (
                        <div className="mt-2 flex items-center text-green-600">
                          <Check className="w-4 h-4 mr-1"/>
                          <span className="text-sm">Selecionado</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {responsaveis.length > 0 && (
              <Button 
                variant="ghost" 
                className="w-full text-purple-600"
                onClick={() => setAddResponsavelOpen(true)}
              >
                <UserPlus className="w-4 h-4 mr-2"/>
                Adicionar Outro Responsável
              </Button>
            )}

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => setResponsaveisModalOpen(false)}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-green-500 to-green-600"
                onClick={sendWhatsAppToResponsavel}
                disabled={!selectedResponsavel && responsaveis.length > 0}
              >
                <Smartphone className="w-4 h-4 mr-2"/>
                Enviar WhatsApp
              </Button>
            </div>

            <p className="text-xs text-gray-500 text-center">
              A carteirinha será enviada com frente, verso e link para download do PDF
            </p>
          </div>
        </DialogContent>
      </Dialog>

      {/* Modal de Adicionar Novo Responsável */}
      <Dialog open={addResponsavelOpen} onOpenChange={setAddResponsavelOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600"/>
              Adicionar Novo Responsável
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nome Completo *</Label>
              <Input 
                placeholder="Nome do responsável"
                value={newResponsavel.name}
                onChange={(e) => setNewResponsavel({...newResponsavel, name: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>CPF</Label>
                <Input 
                  placeholder="000.000.000-00"
                  value={newResponsavel.cpf}
                  onChange={(e) => setNewResponsavel({...newResponsavel, cpf: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Celular/WhatsApp *</Label>
                <Input 
                  placeholder="(11) 99999-9999"
                  value={newResponsavel.cellphone}
                  onChange={(e) => setNewResponsavel({...newResponsavel, cellphone: e.target.value})}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>E-mail</Label>
              <Input 
                type="email"
                placeholder="email@exemplo.com"
                value={newResponsavel.email}
                onChange={(e) => setNewResponsavel({...newResponsavel, email: e.target.value})}
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Parentesco</Label>
                <Select 
                  value={newResponsavel.parentesco} 
                  onValueChange={(v) => setNewResponsavel({...newResponsavel, parentesco: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Pai">Pai</SelectItem>
                    <SelectItem value="Mãe">Mãe</SelectItem>
                    <SelectItem value="Avô">Avô</SelectItem>
                    <SelectItem value="Avó">Avó</SelectItem>
                    <SelectItem value="Tio">Tio</SelectItem>
                    <SelectItem value="Tia">Tia</SelectItem>
                    <SelectItem value="Responsável">Responsável</SelectItem>
                    <SelectItem value="Outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Vínculo</Label>
                <Select 
                  value={newResponsavel.tipo_vinculo} 
                  onValueChange={(v) => setNewResponsavel({...newResponsavel, tipo_vinculo: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="financeiro_opcional">Financeiro Opcional</SelectItem>
                    <SelectItem value="pedagogico">Pedagógico</SelectItem>
                    <SelectItem value="principal">Principal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => {
                  setAddResponsavelOpen(false);
                  setNewResponsavel({
                    name: '', cpf: '', cellphone: '', email: '', parentesco: 'Responsável', tipo_vinculo: 'financeiro_opcional'
                  });
                }}
              >
                Cancelar
              </Button>
              <Button 
                className="flex-1 bg-gradient-to-r from-purple-600 to-purple-700"
                onClick={handleAddResponsavel}
                disabled={savingResponsavel || !newResponsavel.name || !newResponsavel.cellphone}
              >
                {savingResponsavel ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin"/>Salvando...</>
                ) : (
                  <><Check className="w-4 h-4 mr-2"/>Salvar Responsável</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
